// services/adminRoleService.ts
// Admin role management: list admins, assign roles/scope, view action logs.
// All writes go through callable Cloud Functions and are audit-logged via adminActionLogs/.

import {
    collection,
    doc,
    getDocs,
    getDoc,
    query,
    orderBy,
    limit,
    where,
} from 'firebase/firestore';
import type {
    Admin as SharedAdmin,
    AdminRole,
    AdminScope,
    SetAdminRoleRequest,
    SetAdminRoleResponse,
    GetAdminAuditLogRequest,
    GetAdminAuditLogResponse,
    AdminAuditLogEntry,
} from '@omore/shared-types';
import { db } from '../firebase/config';
import { callWithIdempotency } from '../firebase/callable';

// Re-export shared enums for downstream consumers (UI, tests, other services).
export type { AdminRole, AdminScope, SetAdminRoleRequest, SetAdminRoleResponse };

export const ALL_ADMIN_ROLES: AdminRole[] = [
    'super_admin',
    'regional_admin',
    'content_moderator',
    'support_agent',
];

export const ROLE_LABELS: Record<AdminRole, string> = {
    super_admin: 'Super Admin',
    regional_admin: 'Regional Admin',
    content_moderator: 'Content Moderator',
    support_agent: 'Support Agent',
};

// Local AdminRecord is the client-side projection of the shared Admin entity plus
// denormalized display fields (email, displayName) that the admin-panel resolves
// from accounts/. Structurally a superset of SharedAdmin.
export interface AdminRecord {
    accountId: string;
    email: string;
    displayName?: string;
    roles: AdminRole[];
    adminScope: AdminScope;
    createdAt: Date;
    updatedAt: Date;
}

// Structural compile-time check: AdminRecord widens the shared Admin contract.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _SharedAdminFieldsCovered = SharedAdmin['accountId'] extends AdminRecord['accountId']
    ? true
    : never;

// Local AdminActionLog exposes Firestore-Date-typed timestamps to the UI. The
// shared AdminAuditLogEntry (numeric millis) is used at the callable boundary.
export interface AdminActionLog {
    id: string;
    action: string;
    adminAccountId: string;
    adminEmail: string;
    targetAccountId?: string;
    details?: Record<string, unknown>;
    timestamp: Date;
}

function tsToDate(ts: unknown): Date {
    if (!ts) return new Date();
    if (ts instanceof Date) return ts;
    const anyTs = ts as { toDate?: () => Date; seconds?: number };
    if (typeof anyTs.toDate === 'function') return anyTs.toDate();
    if (typeof anyTs.seconds === 'number') return new Date(anyTs.seconds * 1000);
    return new Date(ts as string | number);
}

/**
 * Fetch all admin records from admins/ collection (read-only — no direct writes here).
 */
export async function listAdmins(): Promise<AdminRecord[]> {
    const snap = await getDocs(
        query(collection(db, 'admins'), orderBy('createdAt', 'desc'))
    );
    return snap.docs.map(d => {
        const data = d.data();
        return {
            accountId: d.id,
            email: data.email || '',
            displayName: data.displayName,
            roles: (data.roles || []) as AdminRole[],
            adminScope: data.adminScope || {},
            createdAt: tsToDate(data.createdAt),
            updatedAt: tsToDate(data.updatedAt),
        };
    });
}

/**
 * Fetch a single admin record.
 */
export async function getAdminRecord(accountId: string): Promise<AdminRecord | null> {
    const snap = await getDoc(doc(db, 'admins', accountId));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
        accountId: snap.id,
        email: data.email || '',
        displayName: data.displayName,
        roles: (data.roles || []) as AdminRole[],
        adminScope: data.adminScope || {},
        createdAt: tsToDate(data.createdAt),
        updatedAt: tsToDate(data.updatedAt),
    };
}

/**
 * Assign roles and scope to an admin account.
 * Backed by the setAdminRole callable CF which also updates custom claims and logs the action.
 * Requires the calling user to be a super_admin.
 *
 * The callable is invoked with an idempotency key so a retried invocation replays
 * the same logical operation server-side.
 */
export async function setAdminRole(params: SetAdminRoleRequest): Promise<SetAdminRoleResponse> {
    return callWithIdempotency<SetAdminRoleRequest, SetAdminRoleResponse>(
        'setAdminRole',
        params
    );
}

/**
 * Look up an account from the accounts/ collection by email (for the "Add Admin" flow).
 */
export async function lookupAccountByEmail(
    email: string
): Promise<{ accountId: string; displayName?: string; primaryEmail: string } | null> {
    const lower = email.toLowerCase().trim();
    const indexSnap = await getDoc(doc(db, 'emailIndex', lower));
    if (!indexSnap.exists()) return null;
    const { accountId } = indexSnap.data();
    const accountSnap = await getDoc(doc(db, 'accounts', accountId));
    if (!accountSnap.exists()) return null;
    const data = accountSnap.data();
    return {
        accountId,
        primaryEmail: data.primaryEmail || email,
        displayName: data.displayName,
    };
}

/**
 * Fetch adminActionLogs for a specific admin (most recent first).
 *
 * Prefer the getAdminAuditLog callable when a server-side view is needed;
 * the direct read here is retained for the existing UI and reads only.
 */
export async function fetchAdminActionLogs(
    adminAccountId: string,
    limitCount: number = 50
): Promise<AdminActionLog[]> {
    const snap = await getDocs(
        query(
            collection(db, 'adminActionLogs'),
            where('adminAccountId', '==', adminAccountId),
            orderBy('timestamp', 'desc'),
            limit(limitCount)
        )
    );
    return snap.docs.map(d => mapLogDoc(d));
}

/**
 * Fetch recent adminActionLogs across all admins (for a global audit view).
 */
export async function fetchRecentActionLogs(limitCount: number = 100): Promise<AdminActionLog[]> {
    const snap = await getDocs(
        query(
            collection(db, 'adminActionLogs'),
            orderBy('timestamp', 'desc'),
            limit(limitCount)
        )
    );
    return snap.docs.map(d => mapLogDoc(d));
}

/**
 * Fetch the audit log via the getAdminAuditLog callable and materialise the
 * numeric-millis entries into Date-typed AdminActionLog rows for the UI.
 */
export async function getAdminAuditLogViaCallable(
    params: GetAdminAuditLogRequest
): Promise<{
    entries: AdminActionLog[];
    nextCursorTimestampMillis?: number;
}> {
    const data = await callWithIdempotency<GetAdminAuditLogRequest, GetAdminAuditLogResponse>(
        'getAdminAuditLog',
        params
    );
    return {
        entries: data.entries.map(auditEntryToActionLog),
        nextCursorTimestampMillis: data.nextCursorTimestampMillis,
    };
}

function auditEntryToActionLog(e: AdminAuditLogEntry): AdminActionLog {
    return {
        id: e.entryId,
        action: e.action,
        adminAccountId: e.adminAccountId,
        adminEmail: e.adminEmail,
        targetAccountId: e.targetAccountId,
        details: e.details,
        timestamp: new Date(e.timestampMillis),
    };
}

function mapLogDoc(d: { id: string; data: () => Record<string, unknown> }): AdminActionLog {
    const data = d.data();
    return {
        id: d.id,
        action: (data.action as string) || '',
        adminAccountId: (data.adminAccountId as string) || '',
        adminEmail: (data.adminEmail as string) || '',
        targetAccountId: data.targetAccountId as string | undefined,
        details: data.details as Record<string, unknown> | undefined,
        timestamp: tsToDate(data.timestamp),
    };
}
